import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
    Check,
    Minus,
    Cpu,
    Activity,
    Sparkles,
    Sliders,
    X,
    ChevronDown,
    Layers,
    Battery,
    Shield,
    Heart,
    Moon,
    Thermometer,
    Zap,
    Footprints,
    Share2,
    Radio,
    Bluetooth,
    Eye,
    Scale,
    Waves,
    Info,
} from "lucide-react";
import SectionHeader from "../SectionHeader";
import { ProductComparisonProps, ProductModel, BaseFeature } from "./deta-ProductComparison/types";
import { comparisonTranslations } from "./deta-ProductComparison/comparisonTranslations";

type ComparisonDict = any;

// ─── Semantic Feature Icons Helper ─────────────────────────────────────────────
const getFeatureIcon = (featureName: string) => {
    switch (featureName) {
        case "ضربان قلب":
            return <Heart className="w-4 h-4 text-rose-500" />;
        case "سطح اکسیژن خون (SpO2)":
        case "ECG (نوار قلب)":
        case "تغییرات HRV":
        case "VO2max":
        case "پایش سلامت پیوسته و ۲۴ ساعته":
            return <Activity className="w-4 h-4 text-blue-500" />;
        case "پایش خواب":
            return <Moon className="w-4 h-4 text-indigo-500" />;
        case "دمای پوست":
        case "دمای بدن":
            return <Thermometer className="w-4 h-4 text-amber-500" />;
        case "سطح استرس":
        case "روند انرژی و متابولیسم":
        case "پایش گردش خون":
            return <Zap className="w-4 h-4 text-orange-500" />;
        case "قدم‌شمار":
        case "حالت ورزشی":
        case "تعداد حالت‌های ورزشی":
        case "مود ورزشی":
            return <Footprints className="w-4 h-4 text-emerald-500" />;
        case "وزن":
            return <Scale className="w-4 h-4 text-slate-500" />;
        case "جنس بدنه":
        case "جنس بند":
        case "رنگ‌بندی":
            return <Layers className="w-4 h-4 text-slate-500" />;
        case "نمایشگر":
            return <Eye className="w-4 h-4 text-cyan-500" />;
        case "گواهی ضد آب":
            return <Waves className="w-4 h-4 text-sky-500" />;
        case "عمر باتری":
        case "ظرفیت باتری (mAh)":
        case "زمان آماده‌باش":
        case "نوع شارژ":
        case "شارژ بی‌سیم":
            return <Battery className="w-4 h-4 text-emerald-600" />;
        case "بلوتوث":
        case "به‌روزرسانی OTA":
            return <Bluetooth className="w-4 h-4 text-indigo-500" />;
        case "اشتراک‌گذاری سلامت با خانواده و اعضا":
        case "تماس اضطراری":
            return <Share2 className="w-4 h-4 text-violet-500" />;
        case "NFC":
        case "ردیابی موقعیت":
            return <Radio className="w-4 h-4 text-amber-600" />;
        default:
            return <Shield className="w-4 h-4 text-slate-400" />;
    }
};

// ─── Optimized List Icons ──────────────────────────────────────────────────────
const CheckIcon: React.FC = () => (
    <motion.div
        className="w-6 h-6 mx-auto rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200/90 flex items-center justify-center shadow-2xs"
        whileHover={{ scale: 1.15 }}
        transition={{ type: "spring", stiffness: 400, damping: 15 }}
    >
        <Check className="w-3.5 h-3.5 stroke-[2.5]" />
    </motion.div>
);

const CrossIcon: React.FC = () => (
    <div className="w-6 h-6 mx-auto rounded-full bg-slate-100/90 text-slate-300 border border-slate-200/60 flex items-center justify-center">
        <Minus className="w-3 h-3 stroke-[2.5]" />
    </div>
);

// ─── Refined Pill Value ────────────────────────────────────────────────────────
export const formatFeatureValue = (
    val: string | boolean | number,
    dict: ComparisonDict
): string | boolean | number => {
    if (typeof val !== "string") return val;
    const trimmed = val.trim();
    if (!trimmed) return val;

    // 1. Direct dictionary match
    if (dict.featureValues && dict.featureValues[trimmed as keyof typeof dict.featureValues]) {
        return dict.featureValues[trimmed as keyof typeof dict.featureValues];
    }

    // 2. Handle slash separated values e.g. "مشکی / نقره‌ای / طلایی" or "Black/Blue/Pink/Silver"
    if (trimmed.includes("/")) {
        const parts = trimmed.split("/").map((p) => p.trim());
        const translatedParts = parts.map((part) => {
            if (dict.featureValues && dict.featureValues[part as keyof typeof dict.featureValues]) {
                return dict.featureValues[part as keyof typeof dict.featureValues];
            }
            return part;
        });
        if (translatedParts.some((p, i) => p !== parts[i])) {
            return translatedParts.join(" / ");
        }
    }

    // 3. Handle comma separated values e.g. "مشکی، نقره‌ای، طلایی" or "مشکی, نقره‌ای, طلایی"
    if (trimmed.includes("،") || trimmed.includes(",")) {
        const delimiter = trimmed.includes("،") ? "،" : ",";
        const parts = trimmed.split(delimiter).map((p) => p.trim());
        const translatedParts = parts.map((part) => {
            if (dict.featureValues && dict.featureValues[part as keyof typeof dict.featureValues]) {
                return dict.featureValues[part as keyof typeof dict.featureValues];
            }
            return part;
        });
        if (translatedParts.some((p, i) => p !== parts[i])) {
            return translatedParts.join(delimiter === "،" ? "، " : ", ");
        }
    }

    return val;
};

const PillValue: React.FC<{ value: string }> = ({ value }) => (
    <span className="inline-block px-3 py-1 rounded-lg text-xs font-semibold bg-slate-100/80 text-slate-700 border border-slate-200/70 transition-all duration-150 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200/80 whitespace-nowrap shadow-2xs">
        {value}
    </span>
);

// ─── Category Tab ─────────────────────────────────────────────────────────────
const CategoryTab: React.FC<{
    label: string;
    active: boolean;
    onClick: () => void;
}> = ({ label, active, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer ${
            active
                ? "bg-[#0038A8] text-white shadow-md shadow-blue-900/20"
                : "bg-slate-100/80 text-slate-600 hover:bg-slate-200/80 hover:text-slate-900"
        }`}
    >
        {label}
    </button>
);

// ─── Product Header Cell ──────────────────────────────────────────────────────
const ProductHeaderCell: React.FC<{
    product: ProductModel;
    onSelectProduct: (product: ProductModel) => void;
    viewSpecsText: string;
}> = ({ product, onSelectProduct, viewSpecsText }) => (
    <th className="py-5 px-3 text-center font-medium border-b border-slate-100 min-w-[140px]">
        <div className="flex flex-col items-center gap-2.5">
            {/* Avatar pedestal */}
            <motion.div
                onClick={() => onSelectProduct(product)}
                className="relative w-18 h-18 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-b from-slate-50 to-slate-100/80 border border-slate-200/80 shadow-xs flex items-center justify-center cursor-pointer group"
                whileHover={{ y: -3, scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
                <img
                    src={product.icon}
                    alt={product.title}
                    className="w-14 h-14 sm:w-16 sm:h-16 object-contain drop-shadow-sm transition-transform duration-300 group-hover:scale-105"
                    style={{ transform: `rotate(${product.rotation || 0}deg)` }}
                />
            </motion.div>

            <span className="text-sm font-bold text-slate-800">{product.title}</span>

            {/* Quick Details Trigger */}
            <button
                type="button"
                onClick={() => onSelectProduct(product)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700 bg-blue-50/80 hover:bg-blue-100/80 px-2.5 py-1 rounded-md transition-colors duration-150 cursor-pointer"
            >
                <Info className="w-3 h-3" />
                <span>{viewSpecsText}</span>
            </button>
        </div>
    </th>
);

// ─── Feature Row ──────────────────────────────────────────────────────────────
const FeatureRow: React.FC<{
    baseFeature: { feature: string; description: string };
    rowIndex: number;
    products: ProductModel[];
    isHovered: boolean;
    onHover: (f: string | null) => void;
    dict: ComparisonDict;
    isRtl: boolean;
}> = ({ baseFeature, rowIndex, products, isHovered, onHover, dict, isRtl }) => {
    const isEven = rowIndex % 2 === 0;
    const localizedName = dict.features[baseFeature.feature as keyof typeof dict.features] || baseFeature.feature;

    return (
        <motion.tr
            className="group"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: Math.min(rowIndex * 0.02, 0.3) }}
            onMouseEnter={() => onHover(baseFeature.feature)}
            onMouseLeave={() => onHover(null)}
        >
            {/* Feature label cell */}
            <td
                className={`py-3.5 px-4 sm:px-6 relative border-t border-slate-100 transition-colors duration-150 ${
                    isHovered ? "bg-blue-50/40" : isEven ? "bg-slate-50/40" : "bg-transparent"
                }`}
            >
                <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 shrink-0 text-slate-500">{getFeatureIcon(baseFeature.feature)}</div>
                    <div>
                        <div className={`font-semibold text-sm transition-colors duration-150 ${isHovered ? "text-blue-700" : "text-slate-800"}`}>
                            {localizedName}
                        </div>
                        {baseFeature.description && (
                            <div className="text-xs text-slate-400 mt-0.5 leading-relaxed line-clamp-1">
                                {(dict.featureDescriptions && dict.featureDescriptions[baseFeature.description as keyof typeof dict.featureDescriptions]) ||
                                    (dict.featureDescriptions && dict.featureDescriptions[baseFeature.feature as keyof typeof dict.featureDescriptions]) ||
                                    baseFeature.description}
                            </div>
                        )}
                    </div>
                </div>
            </td>

            {/* Value cells */}
            {products.map((product) => {
                const pf = product.features.find((f) => f.feature === baseFeature.feature);
                return (
                    <td
                        key={product.id}
                        className={`py-3.5 px-3 text-center border-t border-slate-100 transition-colors duration-150 ${
                            isHovered ? "bg-blue-50/40" : isEven ? "bg-slate-50/40" : "bg-transparent"
                        }`}
                    >
                        {pf ? (
                            typeof pf.value === "boolean" ? (
                                pf.value ? (
                                    <CheckIcon />
                                ) : (
                                    <CrossIcon />
                                )
                            ) : (
                                <PillValue
                                    value={String(formatFeatureValue(pf.value, dict))}
                                />
                            )
                        ) : (
                            <span className="text-slate-300 font-mono text-sm">—</span>
                        )}
                    </td>
                );
            })}
        </motion.tr>
    );
};

// ─── Section Divider Row ──────────────────────────────────────────────────────
const SectionDividerRow: React.FC<{ label: string; colSpan: number; icon: React.ReactNode }> = ({ label, colSpan, icon }) => (
    <tr>
        <td colSpan={colSpan} className="py-4 px-4 sm:px-6 bg-slate-50/70 border-t border-slate-200/80">
            <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-700 bg-white px-3.5 py-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                    {icon}
                    <span>{label}</span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent via-slate-200 to-slate-200" />
            </div>
        </td>
    </tr>
);

// ─── Expand / Collapse Button ─────────────────────────────────────────────────
const ExpandButton: React.FC<{ expanded: boolean; onClick: () => void; viewAllText: string; showLessText: string }> = ({
    expanded,
    onClick,
    viewAllText,
    showLessText,
}) => (
    <motion.button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm text-slate-700 bg-white hover:bg-slate-50 border border-slate-200/90 shadow-sm transition-all duration-200 cursor-pointer"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
    >
        <span>{expanded ? showLessText : viewAllText}</span>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
    </motion.button>
);

// ─── Redesigned Product Specification Modal Card ──────────────────────────────
const ProductSpecModal: React.FC<{
    product: ProductModel;
    allProducts: ProductModel[];
    onSelectProduct: (product: ProductModel) => void;
    onClose: () => void;
    dict: ComparisonDict;
    isRtl: boolean;
}> = ({ product, allProducts, onSelectProduct, onClose, dict, isRtl }) => {
    // Categorize features for the modal
    const hardwareFeatures = product.features.filter((f) => HARDWARE_FEATURES.includes(f.feature));
    const sensorFeatures = product.features.filter((f) => SENSOR_FEATURES.includes(f.feature));
    const smartFeatures = product.features.filter((f) => SMART_FEATURES.includes(f.feature));
    const otherFeatures = product.features.filter(
        (f) => ![...HARDWARE_FEATURES, ...SENSOR_FEATURES, ...SMART_FEATURES].includes(f.feature)
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-3 sm:p-6 pt-16 sm:pt-20 pb-6 overflow-y-auto" dir={isRtl ? "rtl" : "ltr"}>
            {/* Backdrop */}
            <motion.div
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-md"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            />

            {/* Modal Card */}
            <motion.div
                className="relative w-full max-w-2xl bg-white rounded-2xl border border-slate-200/90 shadow-2xl overflow-hidden z-10 flex flex-col max-h-[calc(100vh-5rem)] sm:max-h-[82vh] my-auto"
                initial={{ opacity: 0, scale: 0.96, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 15 }}
                transition={{ type: "spring", stiffness: 380, damping: 26 }}
            >
                {/* Header */}
                <div className="p-4 sm:p-5 border-b border-slate-100 bg-white flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200/80 shadow-2xs flex items-center justify-center shrink-0">
                            <img
                                src={product.icon}
                                alt={product.title}
                                className="w-9 h-9 object-contain"
                                style={{ transform: `rotate(${product.rotation || 0}deg)` }}
                            />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-base sm:text-lg font-bold text-slate-900">{product.title}</h3>
                                {product.category && (
                                    <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-100/80">
                                        {dict.categories[product.category as keyof typeof dict.categories] || product.category}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{dict.fullProductSpecs}</p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex items-center justify-center cursor-pointer"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Model Selector Bar */}
                {allProducts.length > 1 && (
                    <div className="px-5 py-2.5 bg-slate-50/80 border-b border-slate-100 flex items-center gap-3 overflow-x-auto shrink-0 scrollbar-none">
                        <span className="text-xs font-bold text-slate-500 whitespace-nowrap shrink-0">{dict.models}</span>
                        <div className="flex items-center gap-1.5 py-0.5">
                            {allProducts.map((p) => {
                                const isSelected = p.id === product.id;
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => onSelectProduct(p)}
                                        className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all duration-150 cursor-pointer whitespace-nowrap shrink-0 ${
                                            isSelected
                                                ? "bg-[#0038A8] text-white shadow-2xs"
                                                : "bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200/80"
                                        }`}
                                    >
                                        {p.title}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Scrollable Body */}
                <div className="p-5 sm:p-6 overflow-y-auto space-y-6">
                    {/* Hardware Section */}
                    {hardwareFeatures.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <Cpu className="w-4 h-4 text-[#0038A8]" />
                                <h4 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider">{dict.hardwareAndDesign}</h4>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {hardwareFeatures.map((f, i) => (
                                    <div
                                        key={i}
                                        className="p-3 rounded-xl bg-slate-50/60 border border-slate-200/60 flex items-center justify-between gap-3"
                                    >
                                        <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                                            {getFeatureIcon(f.feature)}
                                            <span>{dict.features[f.feature as keyof typeof dict.features] || f.feature}</span>
                                        </div>
                                        <div>
                                            {typeof f.value === "boolean" ? (
                                                f.value ? (
                                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
                                                        <Check className="w-3 h-3" /> {dict.yes}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-400 font-medium">—</span>
                                                )
                                            ) : (
                                                <span className="text-xs font-bold text-slate-900 bg-white px-2.5 py-1 rounded-md border border-slate-200 shadow-2xs">
                                                    {String(formatFeatureValue(f.value, dict))}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Sensor Section */}
                    {sensorFeatures.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <Activity className="w-4 h-4 text-emerald-600" />
                                <h4 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider">{dict.sensorsAndHealth}</h4>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {sensorFeatures.map((f, i) => (
                                    <div
                                        key={i}
                                        className="p-3 rounded-xl bg-slate-50/60 border border-slate-200/60 flex items-center justify-between gap-3"
                                    >
                                        <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                                            {getFeatureIcon(f.feature)}
                                            <span>{dict.features[f.feature as keyof typeof dict.features] || f.feature}</span>
                                        </div>
                                        <div>
                                            {typeof f.value === "boolean" ? (
                                                f.value ? (
                                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
                                                        <Check className="w-3 h-3" /> {dict.yes}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-400 font-medium">—</span>
                                                )
                                            ) : (
                                                <span className="text-xs font-bold text-slate-900 bg-white px-2.5 py-1 rounded-md border border-slate-200 shadow-2xs">
                                                    {String(formatFeatureValue(f.value, dict))}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Smart Features Section */}
                    {smartFeatures.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <h4 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider">{dict.smartCapabilities}</h4>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {smartFeatures.map((f, i) => (
                                    <div
                                        key={i}
                                        className="p-3 rounded-xl bg-slate-50/60 border border-slate-200/60 flex items-center justify-between gap-3"
                                    >
                                        <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                                            {getFeatureIcon(f.feature)}
                                            <span>{dict.features[f.feature as keyof typeof dict.features] || f.feature}</span>
                                        </div>
                                        <div>
                                            {typeof f.value === "boolean" ? (
                                                f.value ? (
                                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
                                                        <Check className="w-3 h-3" /> {dict.active}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-400 font-medium">—</span>
                                                )
                                            ) : (
                                                <span className="text-xs font-bold text-slate-900 bg-white px-2.5 py-1 rounded-md border border-slate-200 shadow-2xs">
                                                    {String(formatFeatureValue(f.value, dict))}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Other Features */}
                    {otherFeatures.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <Sliders className="w-4 h-4 text-slate-600" />
                                <h4 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider">{dict.otherFeatures}</h4>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {otherFeatures.map((f, i) => (
                                    <div
                                        key={i}
                                        className="p-3 rounded-xl bg-slate-50/60 border border-slate-200/60 flex items-center justify-between gap-3"
                                    >
                                        <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                                            {getFeatureIcon(f.feature)}
                                            <span>{dict.features[f.feature as keyof typeof dict.features] || f.feature}</span>
                                        </div>
                                        <div>
                                            {typeof f.value === "boolean" ? (
                                                f.value ? (
                                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
                                                        <Check className="w-3 h-3" /> {dict.yes}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-400 font-medium">—</span>
                                                )
                                            ) : (
                                                <span className="text-xs font-bold text-slate-900 bg-white px-2.5 py-1 rounded-md border border-slate-200 shadow-2xs">
                                                    {String(formatFeatureValue(f.value, dict))}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2 rounded-xl text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 shadow-2xs transition-colors cursor-pointer"
                    >
                        {dict.close}
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

// ─── Group definitions ────────────────────────────────────────────────────────
const SENSOR_FEATURES = [
    "پایش سلامت پیوسته و ۲۴ ساعته",
    "ضربان قلب",
    "تغییرات HRV",
    "سطح استرس",
    "سطح اکسیژن خون (SpO2)",
    "ECG (نوار قلب)",
    "VO2max",
    "حالت ورزشی",
    "تعداد حالت‌های ورزشی",
    "روند انرژی و متابولیسم",
    "پایش خواب",
    "نرخ تنفس",
    "دمای پوست",
    "پایش گردش خون",
    "قدم‌شمار",
];

const HARDWARE_FEATURES = [
    "وزن",
    "جنس بدنه",
    "جنس بند",
    "رنگ‌بندی",
    "نمایشگر",
    "گواهی ضد آب",
    "درجه دقت حسگرها",
    "عمر باتری",
    "ظرفیت باتری (mAh)",
    "زمان آماده‌باش",
    "نوع شارژ",
    "شارژ بی‌سیم",
    "بلوتوث",
    "به‌روزرسانی OTA",
];

const SMART_FEATURES = [
    "اشتراک‌گذاری سلامت با خانواده و اعضا",
    "ژست‌های حرکتی",
    "تماس اضطراری",
    "NFC",
    "بانوان",
    "مود ورزشی",
    "ردیابی موقعیت",
    "API توسعه‌دهنده",
];

// ─── Main Component ───────────────────────────────────────────────────────────
const DynamicProductComparison: React.FC<ProductComparisonProps> = ({
    title,
    subtitle,
    description,
    data,
    defaultCategory = "all",
    showCategories = true,
}) => {
    const { i18n } = useTranslation();
    const lang = (i18n.language || "fa").split("-")[0];
    const langKey = (["fa", "en", "ar", "tr"].includes(lang) ? lang : "fa") as keyof typeof comparisonTranslations;
    const dict = comparisonTranslations[langKey] || comparisonTranslations.fa;
    const isRtl = langKey === "fa" || langKey === "ar";

    const [activeCategory, setActiveCategory] = useState<string>(defaultCategory);
    const [hoveredFeature, setHoveredFeature] = useState<string | null>(null);
    const [selectedModalProduct, setSelectedModalProduct] = useState<ProductModel | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const getFilteredProducts = (): ProductModel[] => {
        const cat = data.categories.find((c) => c.id === activeCategory);
        return cat ? cat.products : data.allProducts;
    };
    const filteredProducts = getFilteredProducts();

    // Categorise base features into groups with defined order
    const sensorFeatures = SENSOR_FEATURES.map((name) => data.baseFeatures.find((f) => f.feature === name)).filter(
        (f): f is BaseFeature => !!f
    );
    const hardwareFeatures = HARDWARE_FEATURES.map((name) => data.baseFeatures.find((f) => f.feature === name)).filter(
        (f): f is BaseFeature => !!f
    );
    const smartFeatures = SMART_FEATURES.map((name) => data.baseFeatures.find((f) => f.feature === name)).filter(
        (f): f is BaseFeature => !!f
    );
    const otherFeatures = data.baseFeatures.filter(
        (f) => ![...SENSOR_FEATURES, ...HARDWARE_FEATURES, ...SMART_FEATURES].includes(f.feature)
    );

    // Flattened ordered list with group headers baked in
    type GroupedRow =
        | { type: "divider"; label: string; icon: React.ReactNode; key: string }
        | { type: "feature"; feature: BaseFeature; key: string };

    const buildRows = (): GroupedRow[] => {
        const rows: GroupedRow[] = [];
        const push = (label: string, icon: React.ReactNode, features: BaseFeature[]) => {
            if (!features.length) return;
            rows.push({ type: "divider", label, icon, key: `div-${label}` });
            features.forEach((f) => rows.push({ type: "feature", feature: f, key: f.feature }));
        };
        push(dict.hardwareAndDesign, <Cpu className="w-3.5 h-3.5 text-blue-600" />, hardwareFeatures);
        push(dict.sensorsAndHealth, <Activity className="w-3.5 h-3.5 text-emerald-600" />, sensorFeatures);
        push(dict.smartCapabilities, <Sparkles className="w-3.5 h-3.5 text-orange-500" />, smartFeatures);
        if (otherFeatures.length) push(dict.otherFeatures, <Sliders className="w-3.5 h-3.5 text-slate-600" />, otherFeatures);
        return rows;
    };

    const allRows = buildRows();
    // Collapsed: show hardware group + first 8 sensors; Expanded: everything
    const COLLAPSED_COUNT = hardwareFeatures.length + 8 + 1;
    const visibleRows = isExpanded ? allRows : allRows.slice(0, COLLAPSED_COUNT + 1);

    let featureCounter = 0;

    return (
        <section dir={isRtl ? "rtl" : "ltr"} id="compare" className="py-12 md:py-20 relative">
            <div className="container mx-auto px-4 relative z-10">
                <SectionHeader regularText={title} highlightedText={subtitle} descriptionText={description} />

                {/* Category tabs */}
                {showCategories && data.categories && data.categories.length > 0 && (
                    <div className="flex flex-col items-center mb-8 gap-2.5">
                        <div className="flex justify-center flex-wrap gap-2">
                            {data.categories.map((cat) => {
                                const catLabel = dict.categories[cat.id as keyof typeof dict.categories] || cat.label;
                                return (
                                    <CategoryTab
                                        key={cat.id}
                                        label={catLabel}
                                        active={activeCategory === cat.id}
                                        onClick={() => setActiveCategory(cat.id)}
                                    />
                                );
                            })}
                        </div>
                        <p className="text-slate-400 text-xs">{dict.selectCategoryHelp}</p>
                    </div>
                )}

                {/* Table Container */}
                <motion.div
                    key={activeCategory}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="rounded-3xl overflow-hidden bg-white/90 backdrop-blur-md border border-slate-200/80 shadow-lg shadow-slate-900/5"
                >
                    <div className="overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
                        <table className="comparison-table w-full border-collapse">
                            <thead>
                                <tr className="bg-slate-50/50">
                                    <th className={`py-5 px-4 sm:px-6 w-[200px] sm:w-[240px] border-b border-slate-100 ${isRtl ? "text-right" : "text-left"}`}>
                                        <span className="text-xs text-slate-400 font-semibold">{dict.featuresAndSpecs}</span>
                                    </th>
                                    {filteredProducts.map((product) => (
                                        <ProductHeaderCell
                                            key={product.id}
                                            product={product}
                                            onSelectProduct={setSelectedModalProduct}
                                            viewSpecsText={dict.viewSpecs}
                                        />
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {visibleRows.map((row) => {
                                    if (row.type === "divider") {
                                        return (
                                            <SectionDividerRow
                                                key={row.key}
                                                label={row.label}
                                                icon={row.icon}
                                                colSpan={filteredProducts.length + 1}
                                            />
                                        );
                                    }
                                    const idx = featureCounter++;
                                    return (
                                        <FeatureRow
                                            key={row.key}
                                            baseFeature={row.feature}
                                            rowIndex={idx}
                                            products={filteredProducts}
                                            isHovered={hoveredFeature === row.feature.feature}
                                            onHover={setHoveredFeature}
                                            dict={dict}
                                            isRtl={isRtl}
                                        />
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Bottom subtle fade when collapsed */}
                    {!isExpanded && (
                        <div
                            className="h-14 w-full pointer-events-none"
                            style={{
                                background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.95))",
                                marginTop: -56,
                            }}
                        />
                    )}
                </motion.div>

                {/* Expand / Collapse Button */}
                <div className="flex justify-center mt-6">
                    <ExpandButton
                        expanded={isExpanded}
                        onClick={() => setIsExpanded((p) => !p)}
                        viewAllText={dict.viewAllSpecs}
                        showLessText={dict.showLess}
                    />
                </div>
            </div>

            {/* Modal Card Popup */}
            <AnimatePresence>
                {selectedModalProduct && (
                    <ProductSpecModal
                        product={selectedModalProduct}
                        allProducts={filteredProducts}
                        onSelectProduct={setSelectedModalProduct}
                        onClose={() => setSelectedModalProduct(null)}
                        dict={dict}
                        isRtl={isRtl}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

export default DynamicProductComparison;

